import { ChangeDetectionStrategy, Component, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { Filter, FilterGroup, FilterOption } from '../models';
import { MarkantElementsModule } from '@markant/elements-angular';
import { TranslocoModule } from '@jsverse/transloco';
import { NgForOf, NgIf } from '@angular/common';
import { ModalBridgeComponent } from '@markant-platform/shared/ui-modal';
import { FilterCheckboxesComponent } from '../filter-checkboxes/filter-checkboxes.component';
import { FilterCountryComponent } from '../filter-country/filter-country.component';

@Component({
  selector: 'markant-platform-filter-menu',
  templateUrl: './filter-menu.component.html',
  styleUrls: ['./filter-menu.component.scss'],
  standalone: true,
  imports: [
    MarkantElementsModule,
    TranslocoModule,
    NgIf,
    NgForOf,
    ModalBridgeComponent,
    FilterCheckboxesComponent,
    FilterCountryComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterMenuComponent implements OnChanges {
  @Input({ required: true })
  set filter(filter: Filter) {
    this.filterGroups = Object.keys(filter)
      .filter(key => filter[key].filterOptions.length > 0)
      .reduce((acc: FilterGroup[][], key: string) => this.mapFilterToNestedFilterArray(filter, key, acc), []);
    this._filter = filter;
  }

  get filter(): Filter {
    return this._filter;
  }

  @Input() disabled = false;
  @Input() showFilterChips = false;
  @Input() preselectedFilter: Record<string, string[]> = {};

  @Output() filterChanged = new Subject<Record<string, string[]>>();

  private _filter: Filter = {};
  filterGroups: FilterGroup[][] = [];
  dialogOpen = false;
  reset$ = new Subject<void>();
  activeFilter: Record<string, string[]> = {};
  activeFilterList: { groupKey: string; filterOption: FilterOption }[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['preselectedFilter'] || changes['filter']) {
      this.updateActiveFilters(this.preselectedFilter);
    }
  }

  private updateActiveFilters(filters: Record<string, string[]>): void {
    if (filters) {
      this.activeFilter = structuredClone(filters);
      this.activeFilterList = this.getActiveFilterList(filters);
    }
  }

  onClose(): void {
    this.activeFilter = structuredClone(this.preselectedFilter);
    this.dialogOpen = false;
  }

  resetFilters(): void {
    this.reset$.next();
  }

  applyFilters(): void {
    this.filterChanged.next(structuredClone(this.activeFilter));
    this.dialogOpen = false;
  }

  updateFilter(updatedActiveFilter: Record<string, string[]>): void {
    this.activeFilter = {
      ...this.activeFilter,
      ...updatedActiveFilter,
    };
  }

  trackByFn(index: number): number {
    return index;
  }

  removeFilter(groupKey: string, filterKey: string): void {
    this.activeFilter = {
      ...this.activeFilter,
      [groupKey]: this.activeFilter[groupKey].filter(key => key !== filterKey),
    };

    this.filterChanged.next(structuredClone(this.activeFilter));
  }

  private getActiveFilterList(
    activeFilter: Record<string, string[]>
  ): { groupKey: string; filterOption: FilterOption }[] {
    return Object.keys(activeFilter)
      .filter(key => this._filter[key])
      .flatMap(key =>
        this._filter[key]?.filterOptions
          .filter(option => activeFilter[key].includes(option.key))
          .map(option => ({ groupKey: key, filterOption: option }))
      );
  }

  private mapFilterToNestedFilterArray(filter: Filter, key: string, acc: FilterGroup[][]): FilterGroup[][] {
    if (filter[key].groupName) {
      const index = acc.findIndex(group => group[0].groupName === filter[key].groupName);

      if (index > -1) {
        acc[index].push({
          key,
          label: filter[key].translationKey || key,
          groupName: filter[key].groupName,
        });
        return acc;
      }

      return [
        ...acc,
        [
          {
            key,
            label: filter[key].translationKey || key,
            groupName: filter[key].groupName,
          },
        ],
      ];
    }

    return [
      ...acc,
      [
        {
          key,
          label: filter[key].translationKey || key,
        },
      ],
    ];
  }
}
